// This component is used to render the parameter canvas in the workspace
// It is responsible for rendering the parameter canvas, handling the form data, preview, and saving the parameter

import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
  Grid,
  FormControlLabel,
  Radio,
  Switch,
  RadioGroup,
  Tooltip,
  Checkbox,
  Snackbar,
} from '@mui/material';
import { DatePicker } from 'rsuite';
import 'rsuite/DateRangePicker/styles/index.css';
import { fontSize, palette } from '../../';
import {
  CloseTwoTone,
  BoltOutlined,
  CloudUploadOutlined,
  InfoOutline,
  RestartAltOutlined,
  WarningAmber,
} from '@mui/icons-material';
import { resetParameterSession } from '../../redux/Parameters/ParametersAction';
import { FormatSettings, FormData, MinMaxPayload } from '../../redux/Parameters/ParameterInterface';
import { NotificationDialog, PopUpSpinner } from '../CommonFunctions/DialogComponents';
import DragColumnsParameterData from './DragColumnsParameterData';
import ParameterPreviewPopUp from './ParameterPreviewPopUp';
import { fetchMinMax, fetchUniqueOptions } from '../../api/filters';
import { getDataSetDetail } from '../../api';
import { checkIfMissingColumn } from '../CommonFunctions/CommonFunctions';
import { RootState } from '../../redux';

const dataTypes = ['number', 'decimal', 'text', 'date', 'boolean'];
const displayTypeMap = {
  number: [
    'input (single selection)',
    'radio (single selection)',
    'checkbox (multiple selection)',
    'slider (single selection)',
    'range (range selection)',
  ],
  decimal: [
    'input (single selection)',
    'radio (single selection)',
    'checkbox (multiple selection)',
    'slider (single selection)',
    'range (range selection)',
  ],
  text: ['input (single selection)', 'radio (single selection)', 'checkbox (multiple selection)'],
  date: [
    'calendar (single selection)',
    'radio (single selection)',
    'checkbox (multiple selection)',
    'calendar (range selection)',
  ],
  boolean: ['switch'],
};

const separatorOptions: any[] = [
  { type: 'None', value: 'None' },
  { type: 'Comma', value: 'Comma' },
  { type: 'Abbrev', value: 'Abbrev' },
];

const formatOptions: any[] = [
  { type: 'Number', value: 'Number' },
  { type: 'Currency', value: 'Currency' },
  { type: 'Percent', value: 'Percent' },
];

const ParameterCanvas = () => {
  const { tabTileProps, parameters, chartProperties, savedCalculations } = useSelector(
    (state: RootState) => ({
      chartProperties: state.chartProperties,
      tabTileProps: state.tabTileProps,
      parameters: state.parameters,
      savedCalculations: state.calculations.savedCalculations,
    }),
  );

  const dispatch = useDispatch();

  const propKey = useMemo(
    () => `${tabTileProps?.selectedTabId}.${tabTileProps?.selectedTileId}`,
    [tabTileProps?.selectedTabId, tabTileProps?.selectedTileId],
  );
  const datasetId =
    parameters.properties[propKey]?.parameterSession?.datasetId ??
    chartProperties.properties[propKey]?.selectedDs?.id ??
    '';

  const workspaceId = tabTileProps?.selectedDataSetList?.find(
    (ds: any) => ds.id === datasetId,
  )?.workSpaceId;

  const mode = parameters.properties[propKey]?.parameterSession?.mode === 'edit';

  const [formData, setFormData] = useState<FormData>({
    dataType: 'text',
    displayType: 'checkbox (multiple selection)',
    defaultValue: '',
    min: '',
    max: '',
    step: '0',
    name: parameters.properties[propKey]?.parameterSession?.name || '',
    boolean: true,
    defaultValueMax: '',
    defaultValueMin: '',
  });
  const [tableData, setTableData] = useState<any>({});
  const [columns, setColumns] = useState<any>(null);
  const [value, setValue] = useState<(string | number)[]>([]);
  const [loadColumn, setLoadColumn] = useState<any>([]);
  const [temp, setTemp] = useState('');
  const [temp1, setTemp1] = useState('');

  const [enableColumn, setEnableColumn] = useState(false);
  const [enableDefaultValue, setEnableDefaultValue] = useState(false);
  const [loadFromColumn, setLoadFromColumn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState<'range' | 'list' | ''>('list');
  const [loadDefaultValue, setLoadDefaultValue] = useState(false);
  const [showPreviewPopUp, setShowPreviewPopUp] = useState(false);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(false);

  const [formatSettings, setFormatSettings] = useState<FormatSettings>({
    formatValue: 'Number',
    currencySymbol: '',
    enableRounding: false,
    roundingDigits: 1,
    numberSeparator: 'None',
  });

  const [alert, setAlert] = useState<{
    severity: 'success' | 'info' | 'warning' | 'error';
    message: string;
    show: boolean;
  }>({
    severity: 'error',
    message: '',
    show: false,
  });

  const [hasDuplicates, setHasDuplicates] = useState(false);

  const inputRef1 = useRef<HTMLInputElement>(null);
  const inputRef2 = useRef<HTMLInputElement>(null);

  const [showWarning, setShowWarning] = useState(false);

  // load dataset for connection id and database schema
  const handleTableData = async () => {
    if (!datasetId || !workspaceId) return;
    const response = await getDataSetDetail(datasetId, workspaceId);
    const dataSetResponse = await response.data;
    setTableData(dataSetResponse);
  };

  // take column name,data type and other metadata and load unique values from that table column on enabling automatically refresh playbook option
  const handleAddColumnData = async (selectedColumn: any) => {
    const payload = {
      tableId: selectedColumn.tableId,
      fieldName: selectedColumn.fieldName,
      dataType: selectedColumn.dataType,
      filterOption: 'allValues',
      timeGrain: selectedColumn.dataType === 'date' ? 'date' : 'year',
    };
    const res = await fetchUniqueOptions(tableData.connectionId, datasetId, workspaceId, payload);

    if (!res.isError && res.data) {
      const result = res.data;

      const unique = Array.from(new Set(result)).sort((a, b) => {
        if (typeof a === 'number' && typeof b === 'number') {
          return a - b;
        }
        return String(a).localeCompare(String(b));
      });
      if (
        active === 'range' &&
        (formData.dataType === 'number' || formData.dataType === 'decimal')
      ) {
        const min = unique.reduce((a: any, b: any) => Math.min(a, b));
        const max = unique.reduce((a: any, b: any) => Math.max(a, b));

        setFormData((prev: any) => ({
          ...prev,
          min,
          max,
          defaultValue: min,
        }));
      } else if (active === 'list' && formData.dataType === 'date') {
        setFormData((prev: any) => ({
          ...prev,
          min: unique[0],
          max: unique[unique.length - 1],
          defaultValue: unique[0],
        }));
      }
      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        const min = unique.reduce((a: any, b: any) => Math.min(a, b));
        const max = unique.reduce((a: any, b: any) => Math.max(a, b));

        setFormData((prev: any) => ({
          ...prev,
          min,
          max,
          defaultValue: min,
        }));
      }
      setLoadColumn(unique);
      setLoading(false);
    } else {
      setLoadColumn([]);
      setLoading(false);
      setAlert({
        severity: 'error',
        message:
          res.responseStatusCode === 422
            ? 'Looks like the column does not exist in the dataset'
            : 'Something went wrong while getting the unique values',
        show: true,
      });
    }
  };

  // load min and max values from a column
  const minMax = async () => {
    const payload: MinMaxPayload = {
      tableId: columns.tableId,
      fieldName: columns.fieldName,
      dataType: columns.dataType === 'number' ? 'integer' : columns.dataType,
      filterOption: 'minMax',
      // timeGrain: columns.dataType === 'date' ? 'date' : 'year',
      timeGrain: columns.dataType === 'date' || columns.dataType === 'timestamp' ? 'date' : 'year',
    };

    if (columns?.calculatedFieldId && columns.has_calculation) {
      const calc = savedCalculations.find(
        (calculation) => calculation.uuid === columns.calculatedFieldId,
      );

      payload.isCalculatedField = columns.has_calculation;
      payload.calculatedField = [
        {
          calculatedFieldName: calc!.calculationInfo.calculatedFieldName ?? '',
          fields: calc!.calculationInfo.fields,
          flows: calc!.calculationInfo.flows,
          conditionFilters: calc!.calculationInfo.conditionFilters,
          overrided: calc!.calculationInfo.overrideCount > 0,
        },
      ];
    }
    console.log(payload);

    const res = await fetchMinMax(tableData.connectionId, datasetId, workspaceId, payload);

    if (!res.isError && res.data) {
      const values = res.data;
      const { min, max } = extractMinMax(values);
      console.log(min, max);
      setFormData((prev: any) => ({
        ...prev,
        min: min?.[0] ?? 0,
        max: max?.[0] ?? 0,
      }));
    } else {
      setEnableColumn(false);
      setAlert({
        severity: 'error',
        message:
          res.responseStatusCode === 422
            ? 'Looks like the column does not exist in the dataset'
            : 'Something went wrong while getting the min max values',
        show: true,
      });
    }
  };

  // to convert the min max property to lower case because the snowflake returns the min max in upper case
  function extractMinMax(obj: any) {
    let min, max;

    for (const key in obj) {
      if (Object.hasOwnProperty.call(obj, key)) {
        const lowerKey = key.toLowerCase();
        if (lowerKey === 'min') {
          min = obj[key];
        } else if (lowerKey === 'max') {
          max = obj[key];
        }
      }
    }

    return { min, max };
  }

  // handle data type change
  const handleDataTypeChange = (e: any) => {
    if (e.target.value === 'text') setActive('list');
    else setActive('range');
    if (e.target.value === 'boolean') setActive('');

    const isAvailable = displayTypeMap[e.target.value as keyof typeof displayTypeMap].includes(
      formData.displayType!,
    );

    const displayType =
      e.target.value === 'boolean'
        ? 'switch'
        : isAvailable
          ? formData.displayType!
          : displayTypeMap[e.target.value as keyof typeof displayTypeMap][1];

    setFormData({
      ...formData,
      dataType: e.target.value,
      displayType: displayType,
      defaultValue: e.target.value === 'boolean' ? true : '',
      min: '',
      max: '',
      step: '',
      defaultValueMin: '',
      defaultValueMax: '',
    });

    setEnableColumn(false);
    setValue([]);
    setLoadColumn([]);
    setColumns(null);
    setPreview(false);
  };

  // handle display type change
  const handleDisplayTypeChange = (e: any) => {
    e.stopPropagation();
    setPreview(false);
    if (['input (single selection)'].includes(e.target.value)) {
      setFormData({
        ...formData,
        displayType: e.target.value,
        defaultValue: '',
        step: '',
        max: '',
        min: '',
        defaultValueMax: '',
        defaultValueMin: '',
      });
      setValue([]);
      setLoadColumn([]);
      setColumns(null);
      return;
    }
    if (
      [
        'range (range selection)',
        'calendar (range selection)',
        'slider (single selection)',
      ].includes(e.target.value)
    ) {
      setActive('range');
    }

    if (columns?.tableId && columns) {
      setFormData({
        ...formData,
        displayType: e.target.value,
      });
      return;
    }
    setFormData({
      ...formData,
      displayType: e.target.value,
      defaultValue: '',
      defaultValueMax: '',
      defaultValueMin: '',
    });
  };

  // handle input change according to the fields
  const handleInputChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (preview) setPreview(false);
    if (enableColumn) {
      if (field === 'min' || field === 'max') {
        setFormData({
          ...formData,
          [field]: e.target.value,
        });
        setEnableColumn(false);
        setOpen(true);
        return;
      }
    }
    if (field === 'defaultValue') {
      if (enableDefaultValue) setEnableDefaultValue(false);
    }
    if (field === 'step') {
      if (Number(e.target.value) < 0) return;
    }
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
    if (field === 'min' || (field === 'max' && columns)) {
      setColumns(null);
      setLoadColumn([]);
    }
  };

  // reset the form data
  const handleReset = () => {
    setFormData((prev) => ({
      ...prev,
      name: parameters.properties[propKey]?.parameterSession?.name ?? '',
      separator: 'None',
      boolean: true,
      defaultValue: '',
      min: '',
      max: '',
      step: '',
      defaultValueMax: '',
      defaultValueMin: '',
    }));
    setEnableColumn(false);
    setValue([]);
    setLoadColumn([]);
    setColumns(null);
  };

  // handle preview parameter to show preview and validate the form data
  const handlePreviewParameter = async (e: any) => {
    e.stopPropagation();
    let preview = false;
    if (formData.displayType !== 'input (single selection)') {
      if (active === 'list') {
        if (value.length === 0) {
          setAlert({
            severity: 'error',
            message: 'Please enter a value in the list for parameter',
            show: true,
          });
          return;
        }
        if (hasDuplicates) {
          setAlert({
            severity: 'warning',
            message: 'Duplicate values found in items. Please remove duplicates before previewing.',
            show: true,
          });
          return;
        }
      }
      if (formData.dataType === 'number' || formData.dataType === 'decimal') {
        if (active === 'range' && (!formData.min || !formData.max)) {
          setAlert({
            severity: 'error',
            message: 'Please enter a minimum and maximum value.',
            show: true,
          });
          return;
        }
        if (active === 'range' && Number(formData.min) > Number(formData.max)) {
          setAlert({
            severity: 'error',
            message: 'Minimum value cannot be greater than maximum value.',
            show: true,
          });
          return;
        }
      }
      if (formData.dataType === 'date') {
        if (active === 'range' && (!formData.min || !formData.max)) {
          setAlert({
            severity: 'error',
            message: 'Please enter a minimum and maximum date.',
            show: true,
          });
          return;
        }
        if (active === 'range' && new Date(formData.min) > new Date(formData.max)) {
          setAlert({
            severity: 'error',
            message: 'Minimum date cannot be greater than maximum date.',
            show: true,
          });
          return;
        }
      }
    }

    if (formData.name.trim().length > 40) {
      setAlert({
        severity: 'error',
        message: 'Parameter name cannot exceed 40 characters',
        show: true,
      });
      return;
    }
    if (formData.dataType === 'boolean') {
      preview = true;
    } else if (
      formData.dataType &&
      formData.displayType &&
      ((formData.max && formData.min) || value.length > 0)
    ) {
      if (formData.name.trim().length > 0) {
        preview = true;
      } else {
        setAlert({
          severity: 'error',
          message: 'Please enter a name for the parameter',
          show: true,
        });
        return;
      }
    } else if (formData.displayType === 'input (single selection)' && formData.name.length > 0) {
      preview = true;
    } else {
      setAlert({
        severity: 'error',
        message: 'Please fill all the fields',
        show: true,
      });
      return;
    }

    if (!mode) {
      if (
        parameters.savedParameters.find(
          (parameter: any) => parameter.name.trim() === formData.name.trim(),
        )
      ) {
        setAlert({
          severity: 'info',
          message: 'Parameter name already exists',
          show: true,
        });
        return;
      }
    }

    // if the user is not importing any data from columns we need to set the default values
    if (!columns) {
      setColumns({
        dataType: formData.dataType,
        displayType: formData.displayType,
        defaultValue: formData.defaultValue,
        fieldName: '',
        tableId: '',
        aggregationType: 'none',
        defaultValueMin: formData.defaultValueMin,
        defaultValueMax: formData.defaultValueMax,
        originalDataType: columns?.originalDataType,
      });
    }
    setShowPreviewPopUp(preview);
  };

  // load column values on load from column
  useEffect(() => {
    setValue(loadColumn);
  }, [loadColumn]);

  // load form data on edit
  useEffect(() => {
    handleTableData();
    if (parameters.properties[propKey]?.parameterSession?.mode === 'edit') {
      setColumns({ ...parameters.properties[propKey]?.parameterSession?.columns });
      setValue(parameters.properties[propKey].parameterSession?.values);
      // setLoadColumn(parameters.properties[propKey]?.parameterSession?.values || []);
      setFormData((prev: any) => ({
        ...prev,
        dataType: parameters.properties[propKey]?.parameterSession?.columns?.dataType,
        displayType: parameters.properties[propKey]?.parameterSession?.columns?.displayType,
        defaultValue: parameters.properties[propKey]?.parameterSession?.columns?.defaultValue,
        min: parameters.properties[propKey]?.parameterSession?.minValue,
        max: parameters.properties[propKey]?.parameterSession?.maxValue,
        step: parameters.properties[propKey]?.parameterSession?.stepValue || '',
        defaultValueMin:
          parameters.properties[propKey]?.parameterSession?.columns?.defaultValueMin || '',
        defaultValueMax:
          parameters.properties[propKey]?.parameterSession?.columns?.defaultValueMax || '',
      }));
      setEnableColumn(parameters.properties[propKey]?.parameterSession?.loadFromColumn || false);
      setEnableDefaultValue(
        parameters.properties[propKey]?.parameterSession?.loadDefaultValue || false,
      );
      setActive(
        parameters.properties[propKey]?.parameterSession?.columns?.dataType !== 'boolean'
          ? parameters.properties[propKey]?.parameterSession?.isRange === 'range'
            ? 'range'
            : 'list'
          : '',
      );
      if (parameters.properties[propKey]?.parameterSession?.formatOptions) {
        setFormatSettings(parameters.properties[propKey]?.parameterSession?.formatOptions);
      }
      setLoadFromColumn(false);
      setLoadDefaultValue(false);
    }
  }, []);

  useEffect(() => {
    const uniqueValues = new Set(value);
    setHasDuplicates(uniqueValues.size !== value.length);
  }, [value]);

  useEffect(() => {
    (async () => {
      if (columns?.displayType === 'input (single selection)' || !columns?.tableId) {
        setShowWarning(false);
      } else {
        const result = await checkIfMissingColumn(
          columns,
          chartProperties.properties[propKey]?.selectedDs,
          tabTileProps.tablesForSelectedDataSets[
            chartProperties.properties[propKey]?.selectedDs.id
          ],
        );
        setShowWarning(result.isMissing);
      }
    })();
  }, [columns]);

  const getMarginTop = (formData: any, active: string) => {
    if (
      formData.displayType === 'checkbox (multiple selection)' ||
      formData.displayType === 'radio (single selection)'
    ) {
      if (formData.dataType === 'text') return '200px';
      if (active === 'list') return '125px';
      if (formData.dataType === 'date') return '-35px';
      return '-120px';
    }

    if (active === 'list') return '125px';
    if (formData.displayType === 'calendar (range selection)') return '-6px';
    if (formData.displayType === 'calendar (single selection)') return '-35px';
    if (
      formData.displayType === 'slider (single selection)' ||
      formData.displayType === 'range (range selection)'
    )
      return '1px';
    if (formData.dataType !== 'text') {
      if (formData.dataType === 'date') return '125px';
      return '67px';
    }
    return '226px';
  };

  const getTopMargin = (formData: any, active: string) => {
    if (
      formData.displayType === 'checkbox (multiple selection)' ||
      formData.displayType === 'radio (single selection)'
    ) {
      if (formData.dataType === 'text') return '195px';
      if (active === 'list') return '125px';
      if (formData.dataType === 'date') return '-37px';
      return '-118px';
    }

    if (active === 'list') return '120px';
    if (formData.displayType === 'calendar (range selection)') return '-5px';
    if (formData.displayType === 'calendar (single selection)') return '-35px';
    if (
      formData.displayType === 'slider (single selection)' ||
      formData.displayType === 'range (range selection)'
    )
      return '0px';
    if (formData.dataType !== 'text') {
      if (formData.dataType === 'date') return '120px';
      return '62px';
    }
    return '225px';
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={open}
        onClose={() => setOpen(false)}
        autoHideDuration={4000}
        message={
          loadColumn.length > 0
            ? enableDefaultValue
              ? 'Now the allowed range is static and it will not load when playbook is opened'
              : 'Now the default value is static and it will not load when playbook is opened'
            : 'Please load some data from a column before enabling this option.'
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            color: '#fff',
            backgroundColor: '#2fcbcd',
            border: '1px solid #2fcbcd',
            borderRadius: '6px',
            padding: '10px',
          },
        }}
      />

      <div
        style={{
          width: '100%',
          display: 'flex',
          // overflowY: 'auto',
          paddingBottom: '0.5rem',
          position: 'relative',
        }}
        onClick={() => {
          setLoadFromColumn(false);
          setLoadDefaultValue(false);
        }}
      >
        <div
          style={{
            width: '100%',
            padding: '0.5rem',
            overflowY: 'auto',
          }}
        >
          <span
            style={{
              fontSize: fontSize.TripleExtraLarge,
              fontWeight: 'bold',
              color: palette.primary.contrastText,
              textAlign: 'center',
              padding: '1rem',
            }}
          >
            {mode ? 'Edit Parameter' : 'Create Parameter'}
          </span>

          <Box
            sx={{
              width: {
                xl: '45%', // Reduced from 35%
                lg: '65%', // Reduced from 45%
                md: '70%', // Reduced from 60%
                sm: '80%', // Reduced from 70%
                xs: '90%', // Reduced from 70%
              },
              maxWidth: 800,
              margin: '0 auto',
              p: 4,
              mb: '0.2rem',
            }}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, md: 6 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'start',
                  }}
                >
                  <Typography fontSize={14}>Data Type</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.dataType}
                      onChange={handleDataTypeChange}
                      size="small"
                      sx={{
                        '.MuiSelect-select': {
                          padding: '8px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'black !important',
                          borderWidth: '1px !important',
                        },
                        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'black !important',
                          borderWidth: '1px !important',
                        },
                      }}
                      disabled={mode}
                    >
                      {dataTypes.map((type, index) => (
                        <MenuItem
                          key={index}
                          value={type}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(201, 201, 201, 0.3)',
                              fontWeight: 600,
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'rgba(201, 201, 201, 0.5)',
                            },
                          }}
                        >
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                {!(
                  formData.displayType === 'input (single selection)' ||
                  formData.displayType === 'slider (single selection)' ||
                  formData.displayType?.includes('range')
                ) && <Divider sx={{ width: '205%', mt: 4, mb: 2 }} />}
                {!(
                  formData.dataType === 'boolean' ||
                  formData.dataType === 'text' ||
                  formData.displayType === 'input (single selection)' ||
                  formData.displayType === 'slider (single selection)' ||
                  formData.displayType?.includes('range')
                ) && (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        alignItems: 'start',
                        transform: 'translateY(-9px)',
                      }}
                    >
                      <Typography fontSize={14} style={{ marginTop: '8px' }}>
                        Populate options using
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'start',
                        marginTop: '0.5rem 0',
                        transform: 'translateY(-7px)',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography
                          color={active === 'range' ? '#2bb9bb' : ''}
                          sx={{ fontSize: '14px' }}
                        >
                          Range
                        </Typography>
                        <Switch
                          checked={active === 'list'}
                          onChange={() => {
                            setActive(active === 'range' ? 'list' : 'range');
                            setPreview(false);
                          }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#2bb9bb',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#2bb9bb',
                            },
                          }}
                        />
                        <Typography
                          color={active === 'list' ? '#2bb9bb' : ''}
                          sx={{ fontSize: '14px' }}
                        >
                          List
                        </Typography>
                      </Box>
                    </div>
                  </>
                )}

                {(formData.displayType === 'slider (single selection)' ||
                  formData.displayType?.includes('range') ||
                  (!['text', 'boolean', 'number', 'decimal', 'date'].includes(
                    formData.dataType ?? '',
                  ) &&
                    formData.displayType !== 'input (single selection)')) && (
                  <Divider
                    sx={{
                      width: '205%',
                      mt:
                        formData.displayType === 'slider (single selection)' ||
                        formData.displayType?.includes('range')
                          ? 3
                          : 1,
                      mb:
                        formData.displayType === 'slider (single selection)' ||
                        formData.displayType?.includes('range')
                          ? 3
                          : 2,
                    }}
                  />
                )}

                {formData.dataType !== 'boolean' &&
                  formData.displayType !== 'input (single selection)' && (
                    <Box
                      sx={{
                        display: 'flex',
                        // flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 2,
                        transform: 'translateY(-7px)',
                      }}
                    >
                      <Typography fontSize={14} style={{ transform: 'translateY(6px)' }}>
                        {`Allowable ${active === 'list' ? 'List' : 'Range'}`}{' '}
                        {showWarning && (
                          <Tooltip
                            title="This column is missing in the dataset"
                            slotProps={{
                              tooltip: {
                                sx: {
                                  fontSize: fontSize.medium,
                                },
                              },
                            }}
                            placement="top"
                          >
                            <WarningAmber
                              style={{
                                color: '#850000ff',
                                marginLeft: '5px',
                                marginBottom: '-2px',
                              }}
                              fontSize="small"
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      <Tooltip
                        title="Reset"
                        placement="top"
                        slotProps={{ tooltip: { sx: { fontSize: fontSize.medium } } }}
                      >
                        <span>
                          <RestartAltOutlined
                            onClick={() => {
                              setValue([]);
                              setLoadColumn([]);
                              setColumns(null);
                              setFormData((prev: FormData) => ({
                                ...prev,
                                min: '',
                                max: '',
                                step: '',
                                defaultValue: '',
                                defaultValueMin: '',
                                defaultValueMax: '',
                              }));
                            }}
                            sx={{
                              color: '#2bbebb',
                              cursor: 'pointer',
                              mr: '0.8rem',
                              mb: -1,
                              opacity:
                                value.length > 1 ||
                                (formData.min !== '' &&
                                  formData.min !== undefined &&
                                  formData.min !== null &&
                                  formData.max !== '' &&
                                  formData.max !== undefined &&
                                  formData.max !== null)
                                  ? 1
                                  : 0.5,
                              pointerEvents:
                                value.length > 1 ||
                                (formData.min !== '' &&
                                  formData.min !== undefined &&
                                  formData.min !== null &&
                                  formData.max !== '' &&
                                  formData.max !== undefined &&
                                  formData.max !== null)
                                  ? 'auto'
                                  : 'none',
                            }}
                            fontSize="small"
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                {formData.displayType !== 'input (single selection)' && (
                  <>
                    {active === 'list' && (
                      <Box
                        sx={{
                          flex: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          height: '15rem',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          mt: 0.5,
                          position: 'relative',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            p: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            fontWeight: 600,
                          }}
                        >
                          Items
                        </Typography>
                        {value.length > 0 && (
                          <Typography
                            variant="body2"
                            sx={{
                              p: 1,
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              position: 'absolute',
                              right: 10,
                              top: 0,
                              mr: '1px',
                            }}
                          >
                            {value.length > 0 ? value.length : value.length}
                          </Typography>
                        )}

                        <Box
                          sx={{
                            flex: 1,
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                              width: '4px',
                              display: 'none',
                            },
                          }}
                        >
                          {value.length > 0
                            ? value.map((text: any, index: number) => (
                                <ListItem
                                  key={index}
                                  text={text}
                                  index={index}
                                  setValue={setValue}
                                  setFormData={setFormData}
                                  setEnableColumn={setEnableColumn}
                                  setPreview={setPreview}
                                  setColumns={setColumns}
                                  setEnableDefaultValue={setEnableDefaultValue}
                                  value={value}
                                />
                              ))
                            : value.map((text, index) => (
                                <ListItem
                                  key={index}
                                  text={text}
                                  index={index}
                                  setValue={setValue}
                                  setFormData={setFormData}
                                  setEnableColumn={setEnableColumn}
                                  setPreview={setPreview}
                                  setColumns={setColumns}
                                  setEnableDefaultValue={setEnableDefaultValue}
                                  value={value}
                                />
                              ))}
                          {(loadColumn.length === 0 || mode) && (
                            <>
                              <TextField
                                ref={inputRef2}
                                placeholder="Add Items"
                                type={
                                  formData.dataType === 'text'
                                    ? 'text'
                                    : formData.dataType === 'date'
                                      ? 'date'
                                      : 'number'
                                }
                                value={temp}
                                tabIndex={1}
                                onKeyUp={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (temp.trim()) {
                                      setValue((prev) => [...prev, temp.trim()]);
                                      setTemp('');
                                    }
                                    setTimeout(() => {
                                      if (inputRef1.current) {
                                        inputRef1.current.focus();
                                      }
                                    }, 0);
                                  }
                                }}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    formData.dataType === 'number' ||
                                    formData.dataType === 'decimal'
                                  ) {
                                    if (!/^[0-9.,]*$/.test(value)) {
                                      return;
                                    }
                                  } else if (formData.dataType === 'date') {
                                    if (!/^[0-9.,-/]*$/.test(value)) {
                                      return;
                                    }
                                  }
                                  setTemp(e.target.value);
                                  if (e.target.value.includes(',')) {
                                    const newValues = e.target.value
                                      .split(',')
                                      .filter((v) => v.trim());
                                    setValue((prev) => [...prev, ...newValues]);
                                    setTemp('');
                                  }
                                }}
                                onBlur={() => {
                                  if (temp.trim()) {
                                    setValue((prev) => [...prev, temp.trim()]);
                                    setTemp('');
                                  }
                                }}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 0,
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'black !important',
                                  },
                                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                    {
                                      borderColor: 'black',
                                      borderWidth: '1px',
                                    },
                                }}
                                autoFocus
                              />

                              {temp.length > 0 && (
                                <TextField
                                  ref={inputRef1}
                                  type={
                                    formData.dataType === 'text'
                                      ? 'text'
                                      : formData.dataType === 'date'
                                        ? 'date'
                                        : 'number'
                                  }
                                  placeholder="Add Items"
                                  value={temp1}
                                  tabIndex={1}
                                  onKeyUp={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (temp1.trim()) {
                                        setValue((prev) => [...prev, temp1.trim()]);
                                        setTemp1('');
                                      }
                                      setTimeout(() => {
                                        if (inputRef2.current) {
                                          inputRef2.current.focus();
                                        }
                                      }, 0);
                                    }
                                  }}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (
                                      formData.dataType === 'number' ||
                                      formData.dataType === 'decimal'
                                    ) {
                                      if (!/^[0-9.,]*$/.test(value)) {
                                        return;
                                      }
                                    } else if (formData.dataType === 'date') {
                                      if (!/^[0-9.,-/]*$/.test(value)) {
                                        return;
                                      }
                                    }
                                    setTemp1(e.target.value);
                                    if (e.target.value.includes(',')) {
                                      const newValues = e.target.value
                                        .split(',')
                                        .filter((v) => v.trim());
                                      setValue((prev) => [...prev, ...newValues]);
                                      setTemp1('');
                                    }
                                  }}
                                  onBlur={() => {
                                    if (temp1.trim()) {
                                      setValue((prev) => [...prev, temp1.trim()]);
                                      setTemp1('');
                                    }
                                  }}
                                  size="small"
                                  fullWidth
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 0,
                                      // borderColor: 'transparent',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'black !important',
                                    },
                                  }}
                                />
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    )}{' '}
                    {active === 'range' && (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1.5,
                          mt: 1,
                          ...(formData.dataType === 'date'
                            ? {
                                flexDirection: 'column',
                                alignItems: '',
                              }
                            : {
                                flex: 1,
                                alignItems: 'center',
                              }),
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                          }}
                        >
                          {formData.dataType === 'date' ? (
                            <>
                              <Typography fontSize={14} minWidth={75} textAlign={'start'}>
                                Minimum:
                              </Typography>
                              <DatePicker
                                style={{ width: '100%' }}
                                value={formData.min ? new Date(formData.min) : null}
                                onChange={(value) => {
                                  if (value) {
                                    const year = value.getFullYear();
                                    const month = String(value.getMonth() + 1).padStart(2, '0');
                                    const day = String(value.getDate()).padStart(2, '0');
                                    const localDate = `${year}-${month}-${day}`;
                                    setFormData({
                                      ...formData,
                                      min: localDate,
                                    });
                                    if (columns) {
                                      setColumns(null);
                                      setLoadColumn([]);
                                    }
                                  } else {
                                    setFormData({
                                      ...formData,
                                      min: '',
                                    });
                                  }
                                }}
                                size="sm"
                                format="yyyy-MM-dd"
                                // placeholder="Select minimum date"
                                oneTap
                                cleanable={false}
                              />
                            </>
                          ) : (
                            <TextField
                              label="Min"
                              value={formData.min}
                              onChange={handleInputChange('min')}
                              size="small"
                              type="number"
                              sx={{
                                '& .MuiInputLabel-root': {
                                  color: ' #666666',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: '#1c1c1c',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: 'black',
                                    borderWidth: '1px',
                                  },
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          {formData.dataType === 'date' ? (
                            <>
                              <Typography fontSize={14} minWidth={75} textAlign={'start'}>
                                Maximum:
                              </Typography>
                              <DatePicker
                                style={{ width: '100%' }}
                                value={formData.max ? new Date(formData.max) : null}
                                onChange={(value) => {
                                  if (value) {
                                    const year = value.getFullYear();
                                    const month = String(value.getMonth() + 1).padStart(2, '0');
                                    const day = String(value.getDate()).padStart(2, '0');
                                    const localDate = `${year}-${month}-${day}`;
                                    setFormData({
                                      ...formData,
                                      max: localDate,
                                    });

                                    if (columns) {
                                      setColumns(null);
                                      setLoadColumn([]);
                                    }
                                  } else {
                                    setFormData({
                                      ...formData,
                                      max: '',
                                    });
                                  }
                                }}
                                size="sm"
                                format="yyyy-MM-dd"
                                // placeholder="Select maximum date"
                                oneTap
                                cleanable={false}
                              />
                            </>
                          ) : (
                            <TextField
                              label="Max"
                              value={formData.max}
                              onChange={handleInputChange('max')}
                              size="small"
                              type="number"
                              sx={{
                                '& .MuiInputLabel-root': {
                                  color: ' #666666',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: '#1c1c1c',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: 'black',
                                    borderWidth: '1px',
                                  },
                              }}
                            />
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          {formData.dataType !== 'date' && (
                            <TextField
                              label="Step"
                              value={formData.step}
                              onChange={handleInputChange('step')}
                              // disabled={!isStepEnabled()}
                              size="small"
                              type="number"
                              sx={{
                                '& .MuiInputLabel-root': {
                                  color: ' #666666',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                  color: '#1c1c1c',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: 'black',
                                    borderWidth: '1px',
                                  },
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'start',
                    marginTop: '1rem',
                  }}
                >
                  <Typography
                    fontSize={14}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: '4px',
                      marginBottom: '2px',
                    }}
                  >
                    Default value{' '}
                    <Tooltip
                      title="The default value must be within the defined range/list. Values outside this range/list will be ignored."
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: fontSize.medium,
                          },
                        },
                      }}
                    >
                      <InfoOutline
                        sx={{
                          fontSize: fontSize.large,
                          marginTop: '1px',
                          marginLeft: '3px',
                          padding: '0.5px',
                          visibility:
                            formData.displayType === 'input (single selection)'
                              ? 'hidden'
                              : 'visible',
                        }}
                      />
                    </Tooltip>{' '}
                  </Typography>
                </div>
                {formData.dataType === 'boolean' ? (
                  <FormControl fullWidth>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group"
                      name="row-radio-buttons-group"
                      value={formData.boolean}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          boolean: value === 'true' ? true : false,
                          defaultValue: value === 'true' ? true : false,
                        });
                      }}
                    >
                      <FormControlLabel value="true" control={<Radio />} label="True" />
                      <FormControlLabel value="false" control={<Radio />} label="False" />
                    </RadioGroup>
                  </FormControl>
                ) : formData.displayType === 'calendar (range selection)' ||
                  formData.displayType === 'range (range selection)' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      ...(formData.dataType === 'date'
                        ? {
                            flexDirection: 'column',
                            alignItems: '',
                          }
                        : {
                            flex: 1,
                            alignItems: 'center',
                          }),
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%',
                      }}
                    >
                      {formData.dataType === 'date' ? (
                        <>
                          <Typography fontSize={14} minWidth={75} textAlign={'start'}>
                            Minimum:
                          </Typography>
                          <DatePicker
                            style={{ width: '100%' }}
                            value={
                              formData.defaultValueMin ? new Date(formData.defaultValueMin) : null
                            }
                            onChange={(value) => {
                              if (value) {
                                const year = value.getFullYear();
                                const month = String(value.getMonth() + 1).padStart(2, '0');
                                const day = String(value.getDate()).padStart(2, '0');
                                const localDate = `${year}-${month}-${day}`;
                                setFormData({
                                  ...formData,
                                  defaultValueMin: localDate,
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  defaultValueMin: '',
                                });
                              }
                            }}
                            size="sm"
                            format="yyyy-MM-dd"
                            // placeholder="Select minimum date"
                            oneTap
                            cleanable={false}
                          />
                        </>
                      ) : (
                        <TextField
                          label="Min"
                          value={formData.defaultValueMin}
                          onChange={(e) => {
                            const value = e.target.value;

                            setFormData({
                              ...formData,
                              defaultValueMin: value,
                            });
                          }}
                          size="small"
                          type="number"
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: ' #666666',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#1c1c1c',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor: 'black',
                                borderWidth: '1px',
                              },
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      {formData.dataType === 'date' ? (
                        <>
                          <Typography fontSize={14} minWidth={75} textAlign={'start'}>
                            Maximum:
                          </Typography>
                          <DatePicker
                            style={{ width: '100%' }}
                            value={
                              formData.defaultValueMax ? new Date(formData.defaultValueMax) : null
                            }
                            onChange={(value) => {
                              if (value) {
                                const year = value.getFullYear();
                                const month = String(value.getMonth() + 1).padStart(2, '0');
                                const day = String(value.getDate()).padStart(2, '0');
                                const localDate = `${year}-${month}-${day}`;
                                setFormData({
                                  ...formData,
                                  defaultValueMax: localDate,
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  defaultValueMax: '',
                                });
                              }
                            }}
                            size="sm"
                            format="yyyy-MM-dd"
                            // placeholder="Select maximum date"
                            oneTap
                            cleanable={false}
                          />
                        </>
                      ) : (
                        <TextField
                          label="Max"
                          value={formData.defaultValueMax}
                          onChange={(e) => {
                            const value = e.target.value;

                            setFormData({
                              ...formData,
                              defaultValueMax: value,
                            });
                          }}
                          size="small"
                          type="number"
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: ' #666666',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#1c1c1c',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor: 'black',
                                borderWidth: '1px',
                              },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                ) : (
                  <>
                    {formData.dataType === 'date' ? (
                      <DatePicker
                        style={{ width: '100%' }}
                        value={
                          formData.defaultValue ? new Date(formData.defaultValue as string) : null
                        }
                        onChange={(value) => {
                          if (value) {
                            const year = value.getFullYear();
                            const month = String(value.getMonth() + 1).padStart(2, '0');
                            const day = String(value.getDate()).padStart(2, '0');
                            const localDate = `${year}-${month}-${day}`;
                            setFormData({
                              ...formData,
                              defaultValue: localDate,
                            });
                          } else {
                            setFormData({
                              ...formData,
                              defaultValue: '',
                            });
                          }
                        }}
                        format="yyyy-MM-dd"
                        oneTap
                        cleanable={true}
                        disabled={
                          formData.displayType === 'calendar (range selection)' ||
                          formData.displayType === 'range (range selection)'
                        }
                      />
                    ) : (
                      <TextField
                        fullWidth
                        value={formData.defaultValue}
                        onChange={handleInputChange('defaultValue')}
                        size="small"
                        type={formData.dataType === 'text' ? 'text' : 'number'}
                        placeholder={
                          formData.dataType === 'text' ? 'Enter a item' : 'Enter a value'
                        }
                        sx={{
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black',
                            borderWidth: '1px',
                          },
                        }}
                        disabled={
                          formData.displayType === 'calendar (range selection)' ||
                          formData.displayType === 'range (range selection)'
                        }
                      />
                    )}
                  </>
                )}
              </Grid>

              <Grid size={{ xs: 6, md: 6 }}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'start',
                  }}
                >
                  <Typography fontSize={14}>Display Type</Typography>
                  <FormControl fullWidth>
                    <Select
                      value={formData.displayType}
                      onChange={handleDisplayTypeChange}
                      size="small"
                      sx={{
                        '.MuiSelect-select': {
                          padding: '8px',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'black !important',
                          borderWidth: '1px !important',
                        },
                        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'black !important',
                          borderWidth: '1px !important',
                        },
                      }}
                      disabled={mode}
                    >
                      {formData.dataType &&
                        displayTypeMap[formData.dataType as keyof typeof displayTypeMap].map(
                          (type, index) => (
                            <MenuItem
                              key={index}
                              value={type}
                              sx={{
                                '&.Mui-selected': {
                                  backgroundColor: 'rgba(201, 201, 201, 0.3)',
                                  fontWeight: '600',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: 'rgba(201, 201, 201, 0.5)',
                                },
                              }}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </MenuItem>
                          ),
                        )}
                    </Select>
                  </FormControl>
                </div>
                {formData.dataType !== 'boolean' &&
                  formData.displayType !== 'input (single selection)' && (
                    <>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          justifyContent: 'end',
                          width: '100%',
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<CloudUploadOutlined />}
                          size="small"
                          sx={{
                            backgroundColor: 'white',
                            color: '#666666',
                            '&:hover': {
                              boxShadow: '2px 2px 2px #ccc',
                            },
                            border: 'GrayText',
                            boxShadow: '1px 1px 2px #ccc',
                            padding: '0.5rem 0',
                            width: '70%',
                            marginTop:
                              formData.displayType === 'slider (single selection)' ||
                              formData.displayType?.includes('range')
                                ? '75px'
                                : formData.dataType !== 'text'
                                  ? '160px'
                                  : '75px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreview(false);
                            setLoadFromColumn(true);
                            setLoadDefaultValue(false);
                          }}
                        >
                          LOAD FROM COLUMN
                        </Button>
                        <Tooltip
                          title="Automatically refresh the list or range when playbook opens"
                          placement="top"
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: fontSize.medium,
                              },
                            },
                          }}
                        >
                          <Button
                            size="small"
                            sx={{
                              boxShadow: '1px 1px 1px #ccc',
                              padding: '0.5rem 0',
                              color: enableColumn ? '#2bb9bb' : '',
                              backgroundColor: enableColumn ? '#e6f7f9' : '',
                              '&:hover': {
                                backgroundColor: enableColumn ? '#d1f0f4' : '',
                              },
                              width: '30%',
                              marginTop:
                                formData.displayType === 'slider (single selection)' ||
                                formData.displayType?.includes('range')
                                  ? '70px'
                                  : formData.dataType !== 'text'
                                    ? '158px'
                                    : '72px',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreview(false);
                              if (!columns || columns.tableId === '') {
                                setOpen(true);
                                return;
                              }
                              if (!enableColumn && formData.dataType !== 'text') {
                                setEnableColumn(true);
                                if (columns) minMax();
                                return;
                              }
                              if (!enableColumn && formData.dataType === 'text') {
                                setEnableColumn(true);
                                handleAddColumnData(columns);
                                return;
                              }
                              setEnableColumn((prev) => !prev);
                            }}
                          >
                            <BoltOutlined />
                          </Button>
                        </Tooltip>
                      </Box>
                      {formData.displayType !== 'input (single selection)' && (
                        <>
                          <Box
                            sx={{
                              mt:
                                formData.dataType === 'boolean'
                                  ? '2.85rem'
                                  : formData.dataType === 'text'
                                    ? '2.85rem'
                                    : formData.displayType === 'slider (single selection)' ||
                                        formData.displayType === 'range (range selection)'
                                      ? '2.75rem'
                                      : formData.displayType === 'calendar (range selection)'
                                        ? '5.6rem'
                                        : '7.45rem',
                            }}
                          />

                          <Box
                            sx={{
                              m:
                                formData.displayType !== 'input (single selection)'
                                  ? '2.4rem 0'
                                  : '',
                              mt:
                                formData.displayType === 'input (single selection)' ? '2.4rem' : '',
                              display: 'flex',
                              gap: '20px',
                              alignItems: 'center',
                              justifyContent: 'end',
                              width: '100%',
                            }}
                          >
                            <Button
                              variant="contained"
                              startIcon={<CloudUploadOutlined />}
                              size="small"
                              sx={{
                                backgroundColor: 'white',
                                color: '#666666',
                                '&:hover': {
                                  boxShadow: '2px 2px 2px #ccc',
                                },
                                border: 'GrayText',
                                padding: '0.5rem 0',
                                width: '70%',
                                boxShadow: '1px 1px 2px #ccc',
                                gap: '2px',
                                marginTop: getMarginTop(formData, active),
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLoadFromColumn(true);
                                setLoadDefaultValue(true);
                              }}
                            >
                              LOAD FROM COLUMN
                            </Button>
                            <Tooltip
                              title="Automatically refresh the list or range when playbook opens"
                              placement="top"
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: fontSize.medium,
                                  },
                                },
                              }}
                            >
                              <Button
                                size="small"
                                sx={{
                                  boxShadow: '1px 1px 1px #ccc',
                                  padding: '0.5rem 0',
                                  color: enableDefaultValue ? '#2bb9bb' : '',
                                  backgroundColor: enableDefaultValue ? '#e6f7f9' : '',
                                  '&:hover': {
                                    backgroundColor: enableDefaultValue ? '#d1f0f4' : '',
                                  },
                                  width: '30%',
                                  marginTop: getTopMargin(formData, active),
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setPreview(false);
                                  if (!columns || columns.tableId === '') {
                                    setOpen(true);
                                    return;
                                  }
                                  const result = await checkIfMissingColumn(
                                    columns,
                                    chartProperties.properties[propKey]?.selectedDs,
                                    tabTileProps.tablesForSelectedDataSets[
                                      chartProperties.properties[propKey]?.selectedDs.id
                                    ],
                                  );
                                  if (result.isMissing) {
                                    setAlert({
                                      severity: 'error',
                                      message: 'Column is missing in the dataset',
                                      show: true,
                                    });
                                    return;
                                  }
                                  setEnableDefaultValue((prev) => !prev);
                                  setShowPreviewPopUp(false);
                                }}
                              >
                                <BoltOutlined />
                              </Button>
                            </Tooltip>
                          </Box>
                        </>
                      )}
                    </>
                  )}
              </Grid>
            </Grid>

            <Divider
              sx={{
                width: '100%',
                mt:
                  formData.displayType === 'calendar (range selection)'
                    ? 7
                    : active === 'list'
                      ? 3
                      : ['radio (single selection)', 'checkbox (multiple selection)'].includes(
                            formData.displayType as string,
                          ) &&
                          (formData.dataType === 'number' || formData.dataType === 'decimal')
                        ? '-12px'
                        : 3,

                mb: 2,
                ...(formData.dataType !== 'boolean' &&
                  formData.displayType !== 'input (single selection)' && {
                    transform: active === 'list' ? 'translateY(-30px)' : 'translateY(-30px)',
                    mb: active === 'list' ? '-25px' : '-20px',
                  }),
              }}
            />

            {(formData.displayType === 'slider (single selection)' ||
              formData.displayType === 'range (range selection)') && (
              <Box sx={{ width: '100%' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                    }}
                  >
                    <Typography fontSize={14} textAlign={'left'} style={{ marginBottom: '3px' }}>
                      Format Value
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        value={formatSettings.formatValue}
                        onChange={(e) => {
                          setFormatSettings({
                            ...formatSettings,
                            formatValue: e.target.value,
                            currencySymbol: e.target.value === 'Currency' ? '$' : '',
                            numberSeparator:
                              e.target.value === 'Percent'
                                ? 'None'
                                : formatSettings.numberSeparator,
                          });
                          setPreview(false);
                        }}
                        size="small"
                        sx={{
                          '.MuiSelect-select': {
                            padding: '8px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black !important',
                            borderWidth: '1px !important',
                          },
                          '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'black !important',
                            borderWidth: '1px !important',
                          },
                        }}
                      >
                        {formatOptions.map((type, index) => (
                          <MenuItem
                            key={index}
                            value={type.value}
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(201, 201, 201, 0.3)',
                                fontWeight: '600',
                              },
                              '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(201, 201, 201, 0.5)',
                              },
                            }}
                          >
                            {type.type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {formatSettings.formatValue === 'Currency' && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: '0.5rem',
                          gap: '5px',
                        }}
                      >
                        <span
                          style={{
                            color: '#1c1c1c',
                            width: 'fit-content',
                            textWrap: 'nowrap',
                          }}
                        >
                          Currency Symbol
                        </span>
                        <TextField
                          value={formatSettings.currencySymbol}
                          onChange={(e: any) => {
                            setFormatSettings({
                              ...formatSettings,
                              currencySymbol: e.target.value,
                            });
                            setPreview(false);
                          }}
                          size="small"
                          sx={{
                            bgcolor: 'transparent !important',
                            width: '100% !important',
                            '& .MuiInputLabel-root': {
                              color: ' #666666',
                              bgcolor: 'transparent !important',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#1c1c1c',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor: 'black',
                                borderWidth: '1px',
                                bgcolor: 'transparent !important',
                              },
                          }}
                          placeholder="$"
                          autoFocus
                          className="inputValue"
                          type="text"
                          style={{ paddingRight: 0 }}
                        />
                      </div>
                    )}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    {formatSettings.formatValue !== 'Percent' && (
                      <Box
                        sx={{
                          flex: 1,
                        }}
                      >
                        <Typography
                          fontSize={14}
                          textAlign={'left'}
                          style={{ marginBottom: '3px' }}
                        >
                          Label Format
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={formatSettings.numberSeparator}
                            onChange={(e) => {
                              setFormatSettings({
                                ...formatSettings,
                                numberSeparator: e.target.value,
                              });
                              setPreview(false);
                            }}
                            size="small"
                            sx={{
                              '.MuiSelect-select': {
                                padding: '8px',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black !important',
                                borderWidth: '1px !important',
                              },
                              '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: 'black !important',
                                  borderWidth: '1px !important',
                                },
                            }}
                          >
                            {separatorOptions.map((type, index) => (
                              <MenuItem
                                key={index}
                                value={type.value}
                                sx={{
                                  '&.Mui-selected': {
                                    backgroundColor: 'rgba(201, 201, 201, 0.3)',
                                    fontWeight: '600',
                                  },
                                  '&.Mui-selected:hover': {
                                    backgroundColor: 'rgba(201, 201, 201, 0.5)',
                                  },
                                }}
                              >
                                {type.type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0,
                        mt: -1.2,
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          justifyContent: 'space-between',
                          mt: 1,
                          padding: '0',
                          marginBottom: '3px',
                        }}
                      >
                        <Typography fontSize={14} textAlign={'left'}>
                          Round off
                        </Typography>
                        <Checkbox
                          id="enableDisable"
                          style={{
                            transform: 'scale(0.8)',
                          }}
                          sx={{
                            '&.Mui-checked': {
                              color: '#a6a6a6',
                            },
                            '&:hover': {
                              backgroundColor: 'transparent !important',
                            },
                            paddingX: '0',
                            position: 'absolute',
                            top: '-1px',
                            right: formatSettings.formatValue === 'Percent' ? '54%' : '5px',
                          }}
                          onClick={() => {
                            setFormatSettings({
                              ...formatSettings,
                              enableRounding: !formatSettings.enableRounding,
                            });
                            setPreview(false);
                          }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          placeholder="Decimal"
                          value={formatSettings.roundingDigits}
                          onChange={(e) => {
                            setFormatSettings({
                              ...formatSettings,
                              roundingDigits: parseInt(e.target.value),
                            });
                            setPreview(false);
                          }}
                          size="small"
                          type="number"
                          sx={{
                            '& .MuiInputLabel-root': {
                              color: ' #666666',
                              mt: 0.5,
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#1c1c1c',
                            },
                            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                              {
                                borderColor: 'black',
                                borderWidth: '1px',
                              },
                          }}
                        />
                        {/* <span style={{ margin: 'auto -0.9rem' }}>decimal</span> */}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {(formData.displayType === 'slider (single selection)' ||
              formData.displayType === 'range (range selection)') && (
              <Divider sx={{ width: '100%', mt: 4, mb: 2 }} />
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                my: 1.5,
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  // gap: 2,
                  alignItems: 'start',
                  flex: 0.5,
                }}
              >
                <Typography fontSize={14} style={{ marginBottom: '2px' }}>
                  Name
                </Typography>
                <Tooltip
                  title={
                    formData.name.length > 40 ? 'Parameter name cannot exceed 40 characters.' : ''
                  }
                  disableHoverListener={formData.name.length <= 40}
                  placement="right"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '10px',
                      },
                    },
                  }}
                >
                  <TextField
                    value={formData.name}
                    fullWidth
                    onChange={handleInputChange('name')}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: formData.name.length > 40 ? 'red' : 'black',
                        borderWidth: '1px',
                      },
                      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: formData.name.length > 40 ? 'red' : undefined,
                      },
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </div>

        {/* Close & Save & Preview buttons */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            right: '10px',
          }}
        >
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              width: '250px',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
            }}
          >
            <Button
              onClick={(e) => {
                e.stopPropagation();
                dispatch(resetParameterSession(propKey));
              }}
              sx={{
                color: palette.primary.contrastText,
                background: 'white',
                padding: '3px 5px',
                height: '2rem',
                flex: 1,
                border: 'GrayText 1px solid',
                borderRadius: '3px',
                fontSize: fontSize.small,
              }}
            >
              Close
            </Button>
            {!mode && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleReset}
                sx={{
                  color: palette.primary.contrastText,
                  border: 'GrayText 1px solid',
                  paddingX: '1.15rem',
                  width: '100%',
                  padding: '3px 5px',
                  height: '2rem',
                  flex: 1,
                }}
              >
                Reset
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              sx={{
                paddingX: '1.15rem',
                width: '100%',
                height: '2rem',
                flex: 1,
              }}
              onClick={(e) => {
                setLoading(true);
                setTimeout(() => {
                  handlePreviewParameter(e);
                  setLoading(false);
                }, 100);
              }}
            >
              Preview
            </Button>
          </div>
        </Box>
      </div>

      {loading && <PopUpSpinner show={loading} />}

      {loadFromColumn && (
        <DragColumnsParameterData
          propKey={propKey}
          datasetId={datasetId}
          workspaceId={workspaceId}
          setFormData={setFormData}
          formData={formData}
          setLoadColumn={setLoadColumn}
          setLoadFromColumn={setLoadFromColumn}
          loadColumn={loadColumn}
          setLoading={setLoading}
          setAlert={setAlert}
          isRange={active}
          isDate={formData.dataType === 'date'}
          loadDefaultValue={loadDefaultValue}
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          setLoadDefaultValue={setLoadDefaultValue}
          showWarning={showWarning}
        />
      )}

      {showPreviewPopUp && (
        <ParameterPreviewPopUp
          propKey={propKey}
          showPopup={showPreviewPopUp}
          setShowPopup={setShowPreviewPopUp}
          formData={formData}
          value={value}
          setValue={setValue}
          columns={columns}
          setPreview={setPreview}
          preview={preview}
          loadFromColumn={enableColumn}
          loadDefaultValue={enableDefaultValue}
          formatOptions={formatSettings}
          isRange={active}
        />
      )}

      {alert.show && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert.show}
          onCloseAlert={() => {
            setAlert({
              severity: 'success',
              message: '',
              show: false,
            });
          }}
          testMessage={alert.message}
        />
      )}
    </>
  );
};

// List item component to render the column values with optimize performance
const ListItem = memo(
  ({
    text,
    index,
    setValue,
    setFormData,
    setEnableColumn,
    setPreview,
    setColumns,
    setEnableDefaultValue,
    value,
  }: any) => {
    const isDuplicate = value.filter((v: any) => v === text).length > 1;
    const firstIndex = value.indexOf(text);
    const isDuplicateNotFirst = isDuplicate && index !== firstIndex;

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderLeft: isDuplicateNotFirst ? '3px solid red' : undefined,
          '&:hover .default-button': {
            opacity: 1,
          },
        }}
      >
        <Typography>{text}</Typography>
        <Box
          sx={{
            display: text === '(Blank)' || text === '(Empty)' ? 'none' : 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Button
            variant="outlined"
            className="default-button"
            onClick={(e) => {
              setFormData((prev: any) => ({
                ...prev,
                defaultValue: text,
              }));
              setPreview(false);
            }}
            title="Set as default"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s',
              width: '90px',
              height: '20px',
              padding: '0.5px',
              fontSize: '5px',
              borderRadius: '4px',
              '& .MuiButton-startIcon': { margin: 0 },
              '& .MuiButton-endIcon': { margin: 0 },
              '&:hover': {
                backgroundColor: '#00bcd4',
                color: 'white',
                opacity: 1,
              },
            }}
          >
            <span style={{ fontSize: '10px' }}>Set as default</span>
          </Button>
          <IconButton
            size="small"
            onClick={(e) => {
              setColumns(null);
              setValue((prev: string[]) => prev.filter((_, i) => i !== index));
              setEnableColumn(false);
              setEnableDefaultValue(false);
              setFormData((prev: any) => {
                if (prev?.defaultValue === text) {
                  return {
                    ...prev,
                    defaultValue: '',
                  };
                }
                return prev;
              });
            }}
            sx={{
              color: 'red',
            }}
          >
            <CloseTwoTone fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  },
);

export default ParameterCanvas;
